<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="subtitle" />

        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="loading-wrap">
              <div class="loading-spinner"></div>
              <span>Loading sale details...</span>
            </div>

            <div v-else-if="!sale" class="empty-wrap">
              <p class="empty-title">Sale not found</p>
              <router-link to="/saleslist" class="btn-back">Back to Sales</router-link>
            </div>

            <template v-else>
              <div class="detail-toolbar">
                <div>
                  <h3>{{ sale.billno || "No Bill" }}</h3>
                  <p>{{ sale.customername || "Walk-in Customer" }} · {{ formatDate(sale.billdate) }}</p>
                </div>
                <div class="toolbar-actions">
                  <span class="payment-status" :class="paymentStatusClass(creditSummary.payment_status)">
                    {{ paymentStatusLabel(creditSummary.payment_status) }}
                  </span>
                  <router-link to="/saleslist" class="btn-back">Back</router-link>
                </div>
              </div>

              <div class="summary-grid">
                <div class="summary-box">
                  <span>Net Amount</span>
                  <strong>Rs. {{ formatNumber(sale.netamount) }}</strong>
                </div>
                <div class="summary-box">
                  <span>Paid</span>
                  <strong>Rs. {{ formatNumber(creditSummary.paid_amount) }}</strong>
                </div>
                <div class="summary-box">
                  <span>Outstanding</span>
                  <strong>Rs. {{ formatNumber(creditSummary.outstanding_balance) }}</strong>
                </div>
                <div class="summary-box">
                  <span>Payment Mode</span>
                  <strong>{{ sale.paymentmode || "-" }}</strong>
                </div>
              </div>

              <div class="tabs-row">
                <button :class="{ active: activeTab === 'bill' }" @click="activeTab = 'bill'">Bill Details</button>
                <button :class="{ active: activeTab === 'credit' }" @click="activeTab = 'credit'">Credit Details</button>
              </div>

              <div v-if="activeTab === 'bill'" class="tab-panel">
                <div class="info-grid">
                  <div><span>Bill No</span><strong>{{ sale.billno || "-" }}</strong></div>
                  <div><span>Bill Date</span><strong>{{ formatDate(sale.billdate) }}</strong></div>
                  <div><span>Customer</span><strong>{{ sale.customername || "-" }}</strong></div>
                  <div><span>Mobile</span><strong>{{ sale.mobile || "-" }}</strong></div>
                  <div><span>Remarks</span><strong>{{ sale.remarks || "-" }}</strong></div>
                </div>

                <div class="table-responsive detail-table-wrap">
                  <table class="detail-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Free</th>
                        <th>Rate</th>
                        <th>Discount %</th>
                        <th>Tax %</th>
                        <th>Taxable</th>
                        <th>Tax Amt</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in sale.items" :key="item.id || index">
                        <td>{{ index + 1 }}</td>
                        <td>
                          <strong>{{ itemName(item) }}</strong>
                          <small v-if="item.batchno">Batch: {{ item.batchno }}</small>
                        </td>
                        <td>{{ formatQty(item.quantity) }}</td>
                        <td>{{ formatQty(item.freequantity) }}</td>
                        <td>Rs. {{ formatNumber(item.rate) }}</td>
                        <td>{{ formatPercent(item.discount) }}</td>
                        <td>{{ item.taxpercent || 0 }}</td>
                        <td>Rs. {{ formatNumber(item.taxablevalue || item.amount) }}</td>
                        <td>Rs. {{ formatNumber(item.taxamount) }}</td>
                        <td>Rs. {{ formatNumber(rowTotal(item)) }}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr><td colspan="9">Total Amount</td><td>Rs. {{ formatNumber(sale.totalamount) }}</td></tr>
                      <tr><td colspan="9">Discount</td><td>- Rs. {{ formatNumber(sale.discountamount) }}</td></tr>
                      <tr><td colspan="9">Tax Amount</td><td>Rs. {{ formatNumber(sale.taxamount) }}</td></tr>
                      <tr><td colspan="9">Round Off</td><td>Rs. {{ formatNumber(sale.roundoff) }}</td></tr>
                      <tr class="net-row"><td colspan="9">Net Amount</td><td>Rs. {{ formatNumber(sale.netamount) }}</td></tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div v-else class="tab-panel">
                <div class="credit-actions">
                  <button
                    class="btn-add-payment"
                    :disabled="creditSummary.outstanding_balance <= 0"
                    @click="openPaymentModal"
                  >
                    Add Payment
                  </button>
                </div>

                <div class="credit-grid">
                  <div><span>Credit Sale</span><strong>{{ sale.is_credit ? "Yes" : "No" }}</strong></div>
                  <div><span>Due Date</span><strong>{{ formatDate(creditSummary.due_date || sale.due_date) }}</strong></div>
                  <div><span>Initial Paid</span><strong>Rs. {{ formatNumber(sale.paid_amount) }}</strong></div>
                  <div><span>Receipt Paid</span><strong>Rs. {{ formatNumber(creditSummary.receipt_paid_amount) }}</strong></div>
                  <div><span>Total Paid</span><strong>Rs. {{ formatNumber(creditSummary.paid_amount) }}</strong></div>
                  <div><span>Outstanding Balance</span><strong>Rs. {{ formatNumber(creditSummary.outstanding_balance) }}</strong></div>
                </div>

                <div class="section-title-row">
                  <h4>Receipts Paid</h4>
                  <span>{{ receipts.length }} receipt{{ receipts.length === 1 ? "" : "s" }}</span>
                </div>

                <div v-if="!receipts.length" class="empty-receipts">
                  No receipt payments recorded for this bill.
                </div>
                <div v-else class="table-responsive detail-table-wrap">
                  <table class="detail-table">
                    <thead>
                      <tr>
                        <th>Receipt No</th>
                        <th>Date</th>
                        <th>Payment Mode</th>
                        <th>Reference</th>
                        <th>Remarks</th>
                        <th class="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="receipt in receipts" :key="receipt.receiptid">
                        <td>{{ receipt.billno || receipt.receiptid }}</td>
                        <td>{{ formatDate(receipt.receipt_date) }}</td>
                        <td>{{ receipt.payment_mode || "-" }}</td>
                        <td>{{ receipt.reference_no || "-" }}</td>
                        <td>{{ receipt.remarks || "-" }}</td>
                        <td class="text-end">Rs. {{ formatNumber(receipt.amount) }}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr class="net-row">
                        <td colspan="5">Total Receipt Paid</td>
                        <td class="text-end">Rs. {{ formatNumber(creditSummary.receipt_paid_amount) }}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showPaymentModal" class="modal-backdrop-custom">
      <div class="payment-modal">
        <div class="modal-head">
          <h4>Add Payment</h4>
          <button class="modal-close" :disabled="paymentSubmitting" @click="closePaymentModal">x</button>
        </div>
        <div class="modal-body-custom">
          <div class="payment-balance">
            <span>Outstanding</span>
            <strong>Rs. {{ formatNumber(creditSummary.outstanding_balance) }}</strong>
          </div>

          <div class="form-group-custom">
            <label>Amount</label>
            <input
              v-model.number="paymentForm.amount"
              type="number"
              min="0"
              :max="creditSummary.outstanding_balance"
              step="0.01"
              class="form-control"
            />
          </div>
          <div class="form-group-custom">
            <label>Payment Date</label>
            <input v-model="paymentForm.receipt_date" type="date" class="form-control" />
          </div>
          <div class="form-group-custom">
            <label>Payment Mode</label>
            <select v-model="paymentForm.payment_mode" class="form-control">
              <option value="CASH">Cash</option>
              <option value="UPI">UPI</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
              <option value="CHEQUE">Cheque</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div class="form-group-custom">
            <label>Remarks</label>
            <textarea v-model="paymentForm.remarks" class="form-control" rows="2"></textarea>
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn-cancel-payment" :disabled="paymentSubmitting" @click="closePaymentModal">Cancel</button>
          <button class="btn-save-payment" :disabled="paymentSubmitting" @click="submitPayment">
            {{ paymentSubmitting ? "Saving..." : "Save Payment" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

export default {
  name: "sales-details",
  data() {
    return {
      title: "Sales Details",
      activeTab: "bill",
      loading: false,
      itemCatalog: {},
      showPaymentModal: false,
      paymentSubmitting: false,
      paymentForm: {
        amount: 0,
        receipt_date: "",
        payment_mode: "CASH",
        remarks: "",
      },
      sale: null,
      saleid: this.$route.query.id || "",
    };
  },
  computed: {
    subtitle() {
      return this.sale?.billno ? `Bill ${this.sale.billno}` : "View bill and credit details";
    },
    receipts() {
      return this.sale?.receipts || [];
    },
    creditSummary() {
      const netamount = Number(this.sale?.netamount || 0);
      const paid = Number(this.sale?.paid_amount || 0);
      return {
        netamount,
        paid_amount: paid,
        receipt_paid_amount: 0,
        outstanding_balance: Math.max(netamount - paid, 0),
        payment_status: this.sale?.payment_status || "PAID",
        is_credit: Boolean(this.sale?.is_credit),
        due_date: this.sale?.due_date || null,
        ...(this.sale?.credit_summary || {}),
      };
    },
  },
  beforeMount() {
    this.fetchSale();
  },
  methods: {
    async fetchSale() {
      if (!this.saleid) {
        Swal.fire({ icon: "warning", title: "Missing Sale", text: "Sale id is required." });
        return;
      }
      this.loading = true;
      try {
        const response = await axios.get(`/v1/sales/${this.saleid}`);
        this.sale = response.data;
        await this.hydrateItemNames();
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load sale details." });
      } finally {
        this.loading = false;
      }
    },
    async hydrateItemNames() {
      const missingIds = (this.sale?.items || [])
        .filter((item) => item.itemid && !item.itemname && !this.itemCatalog[item.itemid])
        .map((item) => item.itemid);

      await Promise.all([...new Set(missingIds)].map(async (itemid) => {
        try {
          const response = await axios.get(`/v1/sales/items/${itemid}`);
          if (response.data?.itemid) this.itemCatalog[itemid] = response.data.itemname;
        } catch (error) {
          console.error(error);
        }
      }));

      if (!this.sale) return;
      this.sale.items = (this.sale.items || []).map((item) => ({
        ...item,
        itemname: item.itemname || this.itemCatalog[item.itemid] || "",
      }));
    },
    itemName(item) {
      return item.itemname || this.itemCatalog[item.itemid] || "-";
    },
    todayString() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    openPaymentModal() {
      const outstanding = Number(this.creditSummary.outstanding_balance || 0);
      if (outstanding <= 0) return;
      this.paymentForm = {
        amount: outstanding,
        receipt_date: this.todayString(),
        payment_mode: "CASH",
        remarks: "",
      };
      this.showPaymentModal = true;
    },
    closePaymentModal() {
      if (this.paymentSubmitting) return;
      this.showPaymentModal = false;
    },
    async submitPayment() {
      const amount = Number(this.paymentForm.amount || 0);
      const outstanding = Number(this.creditSummary.outstanding_balance || 0);
      if (amount <= 0) {
        Swal.fire({ icon: "warning", title: "Invalid Amount", text: "Payment amount must be greater than zero." });
        return;
      }
      if (amount > outstanding) {
        Swal.fire({ icon: "warning", title: "Invalid Amount", text: "Payment cannot exceed outstanding balance." });
        return;
      }

      this.paymentSubmitting = true;
      try {
        await axios.post(`/v1/sales/${this.saleid}/payments`, {
          amount,
          receipt_date: this.paymentForm.receipt_date || undefined,
          payment_mode: this.paymentForm.payment_mode,
          remarks: this.paymentForm.remarks || undefined,
        });
        this.showPaymentModal = false;
        await this.fetchSale();
        Swal.fire({ icon: "success", title: "Payment Added", timer: 1400, showConfirmButton: false });
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Payment Failed", text: error.response?.data?.detail || "Could not add payment." });
      } finally {
        this.paymentSubmitting = false;
      }
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (isNaN(date)) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatPercent(value) {
      if (value === null || value === undefined || value === "") return "-";
      return `${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
    },
    formatQty(value) {
      return Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
    },
    rowTotal(item) {
      const amount = Number(item.amount || item.taxablevalue || 0);
      const tax = Number(item.taxamount || 0);
      return item.istaxinclusive ? amount : amount + tax;
    },
    paymentStatusLabel(status) {
      const normalized = (status || "PAID").toUpperCase();
      if (normalized === "PARTIAL") return "Partial";
      if (normalized === "UNPAID") return "Unpaid";
      return "Paid";
    },
    paymentStatusClass(status) {
      const normalized = (status || "PAID").toUpperCase();
      return {
        "payment-paid": normalized === "PAID",
        "payment-partial": normalized === "PARTIAL",
        "payment-unpaid": normalized === "UNPAID",
      };
    },
  },
};
</script>

<style scoped>
.loading-wrap,
.empty-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 260px;
  color: #6b7280;
}
.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #f3f4f6;
  border-top-color: #fb923c;
  border-radius: 50%;
  animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.empty-title { margin: 0; font-weight: 700; color: #111827; }
.detail-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}
.detail-toolbar h3 { margin: 0 0 4px; font-size: 1.15rem; font-weight: 800; color: #111827; }
.detail-toolbar p { margin: 0; color: #6b7280; font-size: .86rem; }
.toolbar-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; justify-content: flex-end; }
.btn-back {
  display: inline-flex;
  align-items: center;
  height: 34px;
  padding: 0 14px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #374151;
  font-weight: 700;
  font-size: .82rem;
  text-decoration: none;
}
.payment-status { display: inline-flex; align-items: center; height: 28px; border-radius: 20px; padding: 0 11px; font-size: .76rem; font-weight: 800; text-transform: uppercase; }
.payment-paid { background: #dcfce7; color: #166534; border: 1px solid #86efac; }
.payment-partial { background: #fef3c7; color: #92400e; border: 1px solid #fcd34d; }
.payment-unpaid { background: #fee2e2; color: #b91c1c; border: 1px solid #fca5a5; }
.summary-grid,
.info-grid,
.credit-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}
.summary-box,
.info-grid > div,
.credit-grid > div {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  padding: 12px;
}
.summary-box span,
.info-grid span,
.credit-grid span {
  display: block;
  margin-bottom: 4px;
  color: #6b7280;
  font-size: .72rem;
  font-weight: 800;
  text-transform: uppercase;
}
.summary-box strong,
.info-grid strong,
.credit-grid strong { color: #111827; font-size: .92rem; overflow-wrap: anywhere; }
.tabs-row {
  display: flex;
  gap: 6px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 14px;
}
.tabs-row button {
  border: none;
  background: transparent;
  padding: 9px 12px;
  color: #6b7280;
  font-weight: 800;
  border-bottom: 2px solid transparent;
}
.tabs-row button.active { color: #ea580c; border-bottom-color: #ea580c; }
.tab-panel { min-height: 240px; }
.detail-table-wrap { border: 1px solid #e5e7eb; border-radius: 8px; overflow: auto; }
.detail-table { width: 100%; border-collapse: collapse; min-width: 860px; background: #fff; }
.detail-table th,
.detail-table td { padding: 9px 10px; border-bottom: 1px solid #f1f5f9; font-size: .82rem; text-align: left; vertical-align: top; }
.detail-table th { background: #f9fafb; color: #6b7280; font-size: .72rem; font-weight: 800; text-transform: uppercase; }
.detail-table small { display: block; color: #6b7280; margin-top: 2px; }
.detail-table tfoot td { background: #fff7f0; font-weight: 800; text-align: right; }
.detail-table .net-row td { background: #ffedd5; color: #c2410c; }
.text-end { text-align: right !important; }
.section-title-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin: 16px 0 10px; }
.section-title-row h4 { margin: 0; font-size: 1rem; font-weight: 800; color: #111827; }
.section-title-row span { color: #6b7280; font-size: .82rem; font-weight: 700; }
.empty-receipts { border: 1px dashed #d1d5db; border-radius: 8px; padding: 22px; color: #6b7280; text-align: center; background: #f9fafb; }
.credit-actions { display: flex; justify-content: flex-end; margin-bottom: 12px; }
.btn-add-payment {
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 6px;
  background: #ea580c;
  color: #fff;
  font-size: .82rem;
  font-weight: 800;
}
.btn-add-payment:disabled { background: #d1d5db; cursor: not-allowed; }
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(17, 24, 39, .45);
}
.payment-modal {
  width: min(440px, 100%);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 45px rgba(15, 23, 42, .25);
  overflow: hidden;
}
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
}
.modal-head h4 { margin: 0; font-size: 1rem; font-weight: 800; color: #111827; }
.modal-close {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: #f3f4f6;
  color: #374151;
  font-weight: 800;
}
.modal-body-custom { padding: 16px; }
.payment-balance {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  background: #fff7f0;
}
.payment-balance span { color: #9a3412; font-size: .78rem; font-weight: 800; text-transform: uppercase; }
.payment-balance strong { color: #c2410c; }
.form-group-custom { margin-bottom: 12px; }
.form-group-custom label { display: block; margin-bottom: 5px; color: #6b7280; font-size: .76rem; font-weight: 800; }
.form-group-custom .form-control { min-height: 36px; border: 1px solid #e5e7eb; border-radius: 6px; font-size: .86rem; }
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}
.btn-cancel-payment,
.btn-save-payment {
  height: 34px;
  padding: 0 14px;
  border-radius: 6px;
  font-size: .82rem;
  font-weight: 800;
}
.btn-cancel-payment { border: 1px solid #e5e7eb; background: #fff; color: #374151; }
.btn-save-payment { border: none; background: #ea580c; color: #fff; }
.btn-save-payment:disabled,
.btn-cancel-payment:disabled { opacity: .7; cursor: not-allowed; }
@media (max-width: 575px) {
  .detail-toolbar { flex-direction: column; }
  .toolbar-actions { justify-content: flex-start; }
  .credit-actions { justify-content: flex-start; }
}
</style>
